import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api, errMsg } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';

const SEV_STYLE = { minor: 'bg-amber-100 text-amber-800', major: 'bg-orange-100 text-orange-800', severe: 'bg-rose-100 text-rose-700' };

export default function Health() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const isStaff = !['guardian', 'student'].includes(user?.role);
  const isAdmin = ['global_admin', 'school_admin', 'principal', 'registrar'].includes(user?.role);
  const [tab, setTab] = useState('visits');
  const [statusF, setStatusF] = useState('all');
  const { data: visits } = useQuery({ queryKey: ['h-visits'], queryFn: () => api.get('/health/visits').then((r) => r.data) });
  const { data: incidents } = useQuery({ queryKey: ['h-incidents'], queryFn: () => api.get('/health/incidents').then((r) => r.data) });
  const { data: students } = useQuery({ queryKey: ['students'], queryFn: () => api.get('/students').then((r) => r.data), enabled: isStaff });
  const [vform, setVform] = useState({ student_id: '', reason: '', treatment: '', sent_home: false });
  const [iform, setIform] = useState({ student_id: '', category: '', severity: 'minor', description: '', action_taken: '' });
  const inval = (k) => qc.invalidateQueries({ queryKey: [k] });
  const visitMut = useMutation({
    mutationFn: () => api.post('/health/visits', vform),
    onSuccess: () => { toast.success('Visit recorded - guardians notified'); setVform({ student_id: '', reason: '', treatment: '', sent_home: false }); inval('h-visits'); },
    onError: (e) => toast.error(errMsg(e)),
  });
  const incMut = useMutation({
    mutationFn: () => api.post('/health/incidents', iform),
    onSuccess: () => { toast.success('Incident recorded - guardians notified'); setIform({ student_id: '', category: '', severity: 'minor', description: '', action_taken: '' }); inval('h-incidents'); },
    onError: (e) => toast.error(errMsg(e)),
  });
  const resolveMut = useMutation({
    mutationFn: (id) => api.patch(`/health/incidents/${id}`, { status: 'resolved' }),
    onSuccess: () => { toast.success('Marked resolved'); inval('h-incidents'); }, onError: (e) => toast.error(errMsg(e)),
  });
  const delVisitMut = useMutation({ mutationFn: (id) => api.delete(`/health/visits/${id}`), onSuccess: () => { toast.success('Visit removed'); inval('h-visits'); }, onError: (e) => toast.error(errMsg(e)) });
  const delIncMut = useMutation({ mutationFn: (id) => api.delete(`/health/incidents/${id}`), onSuccess: () => { toast.success('Incident removed'); inval('h-incidents'); }, onError: (e) => toast.error(errMsg(e)) });
  const StudentSelect = ({ value, onChange, testid }) => (
    <select data-testid={testid} value={value} onChange={onChange} className="border border-slate-200 rounded-md px-2 py-2 text-sm bg-white">
      <option value="">Select student...</option>
      {(students || []).filter((s) => s.status === 'active').map((s) => <option key={s.id} value={s.id}>{s.name} - {s.grade}{s.section}</option>)}
    </select>
  );
  const shownIncidents = (incidents || []).filter((d) => statusF === 'all' || d.status === statusF);

  return (
    <div data-testid="health-page">
      <h1 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tight mb-1">Health & Discipline</h1>
      <p className="text-sm text-slate-500 mb-6">Infirmary visit log and incident records - guardians are informed instantly</p>

      <div className="flex gap-2 mb-4">
        {['visits', 'incidents'].map((t) => (
          <button key={t} data-testid={`health-tab-${t}`} onClick={() => setTab(t)}
            className={`text-xs rounded-full px-3.5 py-1.5 border capitalize transition-colors ${tab === t ? 'bg-slate-900 text-white border-slate-900' : 'border-slate-200 hover:border-slate-900'}`}>{t === 'visits' ? 'Infirmary visits' : 'Discipline incidents'}</button>
        ))}
        {tab === 'incidents' && ['all', 'open', 'resolved'].map((s) => (
          <button key={s} data-testid={`incident-filter-${s}`} onClick={() => setStatusF(s)}
            className={`text-xs rounded-full px-3 py-1.5 border capitalize ${statusF === s ? 'bg-orange-600 text-white border-orange-600' : 'border-slate-200'}`}>{s}</button>
        ))}
      </div>

      {tab === 'visits' && (
        <>
          <div className="bg-white border border-slate-200 rounded-md p-4 mb-4" data-testid="visits-list">
            {(visits || []).map((v) => (
              <div key={v.id} className="py-2 border-t border-slate-100 text-sm flex items-center gap-2 flex-wrap" data-testid={`visit-row-${v.id}`}>
                <span className="font-medium">{v.student_name}</span>
                <span className="text-xs text-slate-400">Gr {v.grade} · {(v.created_at || '').slice(0, 16).replace('T', ' ')}</span>
                <span className="text-xs">{v.reason}{v.treatment ? ` - ${v.treatment}` : ''}</span>
                {v.sent_home && <span className="font-mono-label text-[9px] uppercase bg-rose-100 text-rose-700 rounded px-1.5 py-0.5">sent home</span>}
                {isAdmin && <button data-testid={`visit-delete-${v.id}`} onClick={() => delVisitMut.mutate(v.id)} className="ms-auto text-xs underline text-slate-300 hover:text-rose-600">remove</button>}
              </div>
            ))}
            {(visits || []).length === 0 && <p className="text-sm text-slate-400 py-3">No infirmary visits recorded.</p>}
          </div>
          {isStaff && (
            <div className="bg-white border border-slate-200 rounded-md p-4 max-w-2xl" data-testid="visit-create-panel">
              <p className="font-mono-label text-[10px] uppercase text-slate-400 mb-2">Record infirmary visit</p>
              <div className="grid sm:grid-cols-2 gap-2">
                <StudentSelect testid="visit-student-select" value={vform.student_id} onChange={(e) => setVform({ ...vform, student_id: e.target.value })} />
                <Input data-testid="visit-reason-input" placeholder="Reason (fever, injury...)" value={vform.reason} onChange={(e) => setVform({ ...vform, reason: e.target.value })} />
                <Input data-testid="visit-treatment-input" placeholder="Treatment given" value={vform.treatment} onChange={(e) => setVform({ ...vform, treatment: e.target.value })} />
                <label className="flex items-center gap-2 text-sm"><input data-testid="visit-senthome-toggle" type="checkbox" checked={vform.sent_home} onChange={(e) => setVform({ ...vform, sent_home: e.target.checked })} /> Sent home</label>
                <Button data-testid="visit-create-button" className="bg-slate-900 hover:bg-slate-800" disabled={!vform.student_id || !vform.reason || visitMut.isPending} onClick={() => visitMut.mutate()}>Record & notify guardians</Button>
              </div>
            </div>
          )}
        </>
      )}

      {tab === 'incidents' && (
        <>
          <div className="bg-white border border-slate-200 rounded-md p-4 mb-4" data-testid="incidents-list">
            {shownIncidents.map((d) => (
              <div key={d.id} className="py-2 border-t border-slate-100 text-sm flex items-center gap-2 flex-wrap" data-testid={`incident-row-${d.id}`}>
                <span className="font-medium">{d.student_name}</span>
                <span className={`font-mono-label text-[9px] uppercase px-1.5 py-0.5 rounded ${SEV_STYLE[d.severity]}`}>{d.severity}</span>
                <span className="text-xs">{d.category}: {d.description}</span>
                <span className={`font-mono-label text-[9px] uppercase px-1.5 py-0.5 rounded ${d.status === 'resolved' ? 'bg-green-100 text-green-800' : 'bg-slate-100 text-slate-500'}`}>{d.status}</span>
                <span className="ms-auto flex gap-2">
                  {isStaff && d.status === 'open' && <button data-testid={`incident-resolve-${d.id}`} onClick={() => resolveMut.mutate(d.id)} className="text-xs underline text-green-700">resolve</button>}
                  {isAdmin && <button data-testid={`incident-delete-${d.id}`} onClick={() => delIncMut.mutate(d.id)} className="text-xs underline text-slate-300 hover:text-rose-600">remove</button>}
                </span>
              </div>
            ))}
            {shownIncidents.length === 0 && <p className="text-sm text-slate-400 py-3">No incidents on record.</p>}
          </div>
          {isStaff && (
            <div className="bg-white border border-slate-200 rounded-md p-4 max-w-2xl" data-testid="incident-create-panel">
              <p className="font-mono-label text-[10px] uppercase text-slate-400 mb-2">Record incident</p>
              <div className="grid sm:grid-cols-2 gap-2">
                <StudentSelect testid="incident-student-select" value={iform.student_id} onChange={(e) => setIform({ ...iform, student_id: e.target.value })} />
                <Input data-testid="incident-category-input" placeholder="Category (bullying, uniform...)" value={iform.category} onChange={(e) => setIform({ ...iform, category: e.target.value })} />
                <select data-testid="incident-severity-select" value={iform.severity} onChange={(e) => setIform({ ...iform, severity: e.target.value })} className="border border-slate-200 rounded-md px-2 py-2 text-sm bg-white">
                  {['minor', 'major', 'severe'].map((s) => <option key={s}>{s}</option>)}
                </select>
                <Input data-testid="incident-action-input" placeholder="Action taken" value={iform.action_taken} onChange={(e) => setIform({ ...iform, action_taken: e.target.value })} />
                <Input data-testid="incident-desc-input" className="sm:col-span-2" placeholder="What happened?" value={iform.description} onChange={(e) => setIform({ ...iform, description: e.target.value })} />
                <Button data-testid="incident-create-button" className="bg-slate-900 hover:bg-slate-800" disabled={!iform.student_id || !iform.category || !iform.description || incMut.isPending} onClick={() => incMut.mutate()}>Record & notify guardians</Button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
